import React, { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Mail, Plus, Trash2, Save, RefreshCw, Power, PowerOff, X, ChevronDown, ChevronUp, Edit3 } from "lucide-react";

/**
 * SharedMailboxesAdmin — Admin/Master configuration for shared inbox processing.
 * Lets admins add a mailbox (e.g. sales@dutacs.de), assign it to one or more
 * MyTACS spaces, and define per-category rules (Task/Werbung/Ticket/...).
 */
export default function SharedMailboxesAdmin({ lang = "de" }) {
    const [items, setItems] = useState([]);
    const [spaces, setSpaces] = useState([]);
    const [adding, setAdding] = useState(false);
    const [newMb, setNewMb] = useState({ email: "", display_name: "", space_ids: [] });
    const [expandedId, setExpandedId] = useState(null);
    const [syncingId, setSyncingId] = useState(null);
    const t = (de, en) => (lang === "de" ? de : en);

    const load = useCallback(async () => {
        try {
            const [mb, sp] = await Promise.all([
                api.get("/ms/shared-mailboxes"),
                api.get("/spaces"),
            ]);
            setItems(mb.data || []);
            setSpaces(sp.data || []);
        } catch (e) { toast.error(errMsg(e)); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const create = async () => {
        if (!newMb.email.trim() || newMb.space_ids.length === 0) {
            return toast.error(t("E-Mail und mindestens eine Abteilung wählen", "Email and at least one department required"));
        }
        try {
            await api.post("/ms/shared-mailboxes", {
                email: newMb.email.trim(),
                display_name: newMb.display_name.trim() || undefined,
                space_ids: newMb.space_ids,
            });
            toast.success(t("Postfach hinzugefügt", "Mailbox added"));
            setAdding(false);
            setNewMb({ email: "", display_name: "", space_ids: [] });
            await load();
        } catch (e) { toast.error(errMsg(e)); }
    };

    const update = async (id, patch) => {
        try {
            await api.put(`/ms/shared-mailboxes/${id}`, patch);
            toast.success(t("Gespeichert", "Saved"));
            await load();
        } catch (e) { toast.error(errMsg(e)); }
    };

    const remove = async (id) => {
        if (!window.confirm(t("Postfach wirklich entfernen?", "Really remove?"))) return;
        try {
            await api.delete(`/ms/shared-mailboxes/${id}`);
            await load();
        } catch (e) { toast.error(errMsg(e)); }
    };

    const sync = async (id) => {
        setSyncingId(id);
        try {
            const { data } = await api.post(`/ms/shared-mailboxes/${id}/sync`);
            if (data.ok) toast.success(`${data.count || 0} ${t("Mails verarbeitet", "mails processed")}`);
            else toast.error(data.error || "Fehler");
            await load();
        } catch (e) { toast.error(errMsg(e)); }
        finally { setSyncingId(null); }
    };

    return (
        <div data-testid="shared-mailboxes-admin">
            <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-neutral-600">{t("Freigegebene Postfächer (sales@..., itsupport@...) per KI klassifizieren und in Tasks der zugewiesenen Abteilung(en) umwandeln.", "Shared mailboxes (sales@..., itsupport@...) get AI-classified and become tasks in the assigned departments.")}</p>
                <Button size="sm" onClick={() => setAdding(true)} className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="add-shared-mb">
                    <Plus className="w-3.5 h-3.5 mr-1" /> {t("Postfach hinzufügen", "Add mailbox")}
                </Button>
            </div>

            {/* Add form */}
            {adding && (
                <div className="bg-neutral-50 border border-neutral-200 rounded p-3 mb-3 space-y-2" data-testid="add-shared-mb-form">
                    <input value={newMb.email} onChange={(e) => setNewMb({ ...newMb, email: e.target.value })} placeholder="sales@dutacs.de" className="w-full text-sm h-8 border border-neutral-200 rounded px-2" data-testid="new-mb-email" />
                    <input value={newMb.display_name} onChange={(e) => setNewMb({ ...newMb, display_name: e.target.value })} placeholder={t("Anzeigename (optional)", "Display name (optional)")} className="w-full text-sm h-8 border border-neutral-200 rounded px-2" data-testid="new-mb-name" />
                    <div>
                        <p className="text-xs font-semibold text-neutral-700 mb-1">{t("Zugewiesene Abteilung(en)", "Assigned department(s)")}</p>
                        <div className="space-y-1 max-h-40 overflow-y-auto bg-white border border-neutral-200 rounded p-2">
                            {spaces.map((s) => (
                                <label key={s.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-neutral-50 px-1 py-0.5 rounded">
                                    <input
                                        type="checkbox"
                                        checked={newMb.space_ids.includes(s.id)}
                                        onChange={(e) => setNewMb({
                                            ...newMb,
                                            space_ids: e.target.checked
                                                ? [...newMb.space_ids, s.id]
                                                : newMb.space_ids.filter((x) => x !== s.id),
                                        })}
                                        data-testid={`new-mb-space-${s.id}`}
                                    />
                                    {s.name}
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => { setAdding(false); setNewMb({ email: "", display_name: "", space_ids: [] }); }} data-testid="new-mb-cancel">
                            <X className="w-3.5 h-3.5 mr-1" />{t("Abbrechen", "Cancel")}
                        </Button>
                        <Button size="sm" onClick={create} className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="new-mb-save">
                            <Save className="w-3.5 h-3.5 mr-1" />{t("Speichern", "Save")}
                        </Button>
                    </div>
                </div>
            )}

            {/* List */}
            {items.length === 0 ? (
                <p className="text-xs text-neutral-400 italic p-6 text-center">{t("Noch keine Postfächer konfiguriert.", "No mailboxes configured yet.")}</p>
            ) : (
                <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded">
                    {items.map((mb) => {
                        const isOpen = expandedId === mb.id;
                        return (
                            <li key={mb.id} className="px-3 py-2" data-testid={`shared-mb-${mb.id}`}>
                                <div className="flex items-center gap-2">
                                    <Mail className={`w-4 h-4 ${mb.active ? "text-blue-500" : "text-neutral-300"}`} />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold truncate">{mb.email}</p>
                                        <p className="text-[10px] text-neutral-500 truncate">
                                            {mb.display_name && `${mb.display_name} · `}
                                            {(mb.space_ids || []).map((sid) => spaces.find((s) => s.id === sid)?.name).filter(Boolean).join(", ")}
                                            {mb.last_polled_at && ` · ${t("zuletzt:", "last:")} ${new Date(mb.last_polled_at).toLocaleTimeString()}`}
                                        </p>
                                    </div>
                                    <Button size="sm" variant="ghost" onClick={() => sync(mb.id)} disabled={syncingId === mb.id} className="h-7" data-testid={`sync-mb-${mb.id}`}>
                                        <RefreshCw className={`w-3.5 h-3.5 ${syncingId === mb.id ? "animate-spin" : ""}`} />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => update(mb.id, { active: !mb.active })} className="h-7" data-testid={`toggle-mb-${mb.id}`}>
                                        {mb.active ? <Power className="w-3.5 h-3.5 text-emerald-500" /> : <PowerOff className="w-3.5 h-3.5 text-neutral-400" />}
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setExpandedId(isOpen ? null : mb.id)} className="h-7" data-testid={`expand-mb-${mb.id}`}>
                                        {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => remove(mb.id)} className="h-7" data-testid={`delete-mb-${mb.id}`}>
                                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                    </Button>
                                </div>
                                {isOpen && (
                                    <RulesEditor mb={mb} spaces={spaces} onSave={(rules, space_ids) => update(mb.id, { rules, space_ids })} lang={lang} />
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

const CAT_LABEL = {
    task:     { de: "Wichtige Mails → Task", en: "Important → Task" },
    ad:       { de: "Werbung / Newsletter",  en: "Ads / Newsletter" },
    ticket:   { de: "Support-Ticket",        en: "Support ticket" },
    personal: { de: "Persönlich",            en: "Personal" },
    spam:     { de: "Spam",                  en: "Spam" },
};

const CATEGORY_OPTIONS = ["task", "ad", "ticket", "personal", "spam"];

function RulesEditor({ mb, spaces, onSave, lang }) {
    const t = (de, en) => (lang === "de" ? de : en);
    const [rules, setRules] = useState(mb.rules || []);
    const [spaceIds, setSpaceIds] = useState(mb.space_ids || []);

    const setRule = (i, field, value) => {
        setRules((r) => {
            const nr = [...r];
            nr[i] = { ...nr[i], [field]: value };
            return nr;
        });
    };

    const addRule = () => {
        setRules((r) => [...r, {
            category: "task",
            enabled: true,
            action: "task",
            notes: "",
            target_folder: "",
            target_space_ids: [],
        }]);
    };

    const removeRule = (i) => {
        if (!window.confirm(t("Diese Regel wirklich löschen?", "Really delete this rule?"))) return;
        setRules((r) => r.filter((_, idx) => idx !== i));
    };

    return (
        <div className="mt-3 pl-6 pr-2 pb-2 space-y-2" data-testid={`mb-rules-${mb.id}`}>
            <div>
                <p className="text-xs font-semibold mb-1">{t("Abteilungen", "Departments")}</p>
                <div className="flex flex-wrap gap-2">
                    {spaces.map((s) => (
                        <label key={s.id} className="flex items-center gap-1 text-xs cursor-pointer">
                            <input type="checkbox" checked={spaceIds.includes(s.id)} onChange={(e) => {
                                setSpaceIds(e.target.checked ? [...spaceIds, s.id] : spaceIds.filter((x) => x !== s.id));
                            }} data-testid={`mb-${mb.id}-space-${s.id}`} />
                            {s.name}
                        </label>
                    ))}
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold">{t("Regeln pro Kategorie", "Per-category rules")}</p>
                    <Button size="sm" variant="outline" onClick={addRule} className="h-7 text-xs gap-1" data-testid={`mb-${mb.id}-add-rule`}>
                        <Plus className="w-3 h-3" />{t("Regel hinzufügen", "Add rule")}
                    </Button>
                </div>
                <div className="space-y-1">
                    {rules.length === 0 && (
                        <p className="text-xs text-neutral-400 italic px-1.5 py-2">{t("Noch keine Regeln. Klicke auf „Regel hinzufügen\".", "No rules yet. Click 'Add rule'.")}</p>
                    )}
                    {rules.map((r, i) => (
                        <div key={`${r.category}-${i}`} className="grid grid-cols-12 gap-1 items-center bg-neutral-50 rounded px-1.5 py-1" data-testid={`mb-${mb.id}-rule-${i}`}>
                            <select
                                value={r.category}
                                onChange={(e) => setRule(i, "category", e.target.value)}
                                className="col-span-3 text-xs h-7 border border-neutral-200 rounded px-1 font-semibold"
                                data-testid={`mb-${mb.id}-rule-cat-${i}`}
                            >
                                {CATEGORY_OPTIONS.map((c) => (
                                    <option key={c} value={c}>{CAT_LABEL[c]?.[lang === "de" ? "de" : "en"] || c}</option>
                                ))}
                            </select>
                            <input type="checkbox" checked={!!r.enabled} onChange={(e) => setRule(i, "enabled", e.target.checked)} className="col-span-1" data-testid={`mb-${mb.id}-rule-enabled-${i}`} title={t("Aktiv", "Active")} />
                            <select value={r.action} onChange={(e) => setRule(i, "action", e.target.value)} className="col-span-3 text-xs h-7 border border-neutral-200 rounded px-1" data-testid={`mb-${mb.id}-rule-action-${i}`}>
                                <option value="task">{t("→ Task im Space", "→ Create task")}</option>
                                <option value="ignore">{t("→ Ignorieren", "→ Ignore")}</option>
                                <option value="move_folder">{t("→ In Ordner", "→ To folder")}</option>
                            </select>
                            <div className="col-span-4">
                                {r.action === "move_folder" ? (
                                    <input value={r.target_folder || ""} onChange={(e) => setRule(i, "target_folder", e.target.value)} placeholder={t("Ordnername", "Folder name")} className="w-full text-xs h-7 border border-neutral-200 rounded px-2" data-testid={`mb-${mb.id}-rule-target-${i}`} />
                                ) : (
                                    <input value={r.notes || ""} onChange={(e) => setRule(i, "notes", e.target.value)} placeholder={t("Hinweis für KI (z.B. 'NUR Anfragen aus Hamburg')", "AI hint")} className="w-full text-xs h-7 border border-neutral-200 rounded px-2" data-testid={`mb-${mb.id}-rule-notes-${i}`} />
                                )}
                            </div>
                            <button
                                onClick={() => removeRule(i)}
                                className="col-span-1 h-7 flex items-center justify-center text-red-500 hover:bg-red-50 rounded transition"
                                title={t("Regel löschen", "Delete rule")}
                                data-testid={`mb-${mb.id}-rule-delete-${i}`}
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
                <p className="text-[10px] text-neutral-400 mt-1.5 italic">
                    {t("Tipp: Du kannst mehrere Regeln pro Kategorie haben — die erste passende wird angewendet. Im „Hinweis für KI\"-Feld kannst du die Klassifizierung präzisieren.",
                       "Tip: You can have multiple rules per category — the first matching one applies. Use 'AI hint' to refine the classification.")}
                </p>
            </div>

            <Button size="sm" onClick={() => onSave(rules, spaceIds)} className="bg-brand-500 hover:bg-brand-600 text-white" data-testid={`mb-save-${mb.id}`}>
                <Save className="w-3.5 h-3.5 mr-1" /> {t("Speichern", "Save")}
            </Button>
        </div>
    );
}
